// src/features/mensagens/components/lista/ConversaItem.jsx
// ============================================================================
// 💬 CONVERSA ITEM — Fila Swipeable Tipo WhatsApp
// 📦 V1 ENTERPRISE — Swipe Actions · Long Press · Haptic · Native Feel
// 🔄 V2 — Drag & Drop con @dnd-kit
// ============================================================================
//
// REGLAS DE ORO:
// 1. Swipe left → Archivar (fondo verde)
// 2. Swipe right → Eliminar (fondo rojo)
// 3. Long press → Modo selección múltiple
// 4. Touch feedback háptico (vibración) en acciones
// 5. Transiciones fluidas 60fps con framer-motion
// 6. Drag solo desde manija (GripVertical)
// 7. NO romper swipe, long press, ni acciones rápidas
// ============================================================================

import React, { useState, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  MessageCircle,
  Archive,
  Trash2,
  Star,
  Pin,
  Check,
  MoreVertical,
  Clock,
  CheckCheck,
  AlertCircle,
  GripVertical,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import {
  formatConversationTime,
  formatMessagePreview,
  formatUnreadCount,
  formatContactName,
} from '../../utils/formatters';
import {
  MESSAGE_STATUS,
  MESSAGE_STATUS_ICONS,
  MESSAGE_STATUS_COLORS,
  PERFORMANCE,
  UI_DIMENSIONS,
} from '../../utils/constants';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ============================================================================
// 🎨 CONSTANTES DE SWIPE
// ============================================================================

const SWIPE_THRESHOLD = PERFORMANCE.SWIPE_THRESHOLD_PX;
const SWIPE_MAX = 120;
const SWIPE_ARCHIVE_COLOR = '#10b981'; // green-500
const SWIPE_DELETE_COLOR = '#ef4444';  // red-500
const SWIPE_ARCHIVE_BG = 'bg-emerald-500';
const SWIPE_DELETE_BG = 'bg-red-500';

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Fila de conversación con swipe actions tipo WhatsApp + Drag & Drop
 * 
 * @param {Object} props
 * @param {Object} props.conversation - Datos de la conversación
 * @param {boolean} props.isSelected - Si está en modo selección
 * @param {boolean} props.selectionMode - Si el modo selección está activo
 * @param {Function} props.onPress - Al hacer tap
 * @param {Function} props.onLongPress - Al mantener presionado
 * @param {Function} props.onArchive - Al archivar
 * @param {Function} props.onDelete - Al eliminar
 * @param {Function} props.onStar - Al marcar favorito
 * @param {Function} props.onToggleSelect - Al seleccionar/deseleccionar
 */
const ConversaItem = memo(({
  conversation,
  isSelected = false,
  selectionMode = false,
  onPress,
  onLongPress,
  onArchive,
  onDelete,
  onStar,
  onToggleSelect,
}) => {
  // --------------------------------------------------------------------------
  // 📍 HOOKS
  // --------------------------------------------------------------------------
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // --------------------------------------------------------------------------
  // 🔄 DRAG & DROP — useSortable
  // --------------------------------------------------------------------------
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: conversation.id,
    animateLayoutChanges: () => false,
    disabled: selectionMode, // Deshabilitar drag en modo selección
  });

  // Estilo de transformación GPU-accelerated
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    transformOrigin: 'center center',
  };

  // --------------------------------------------------------------------------
  // 📦 ESTADO LOCAL
  // --------------------------------------------------------------------------
  const [swipeX, setSwipeX] = useState(0);
  const [showActions, setShowActions] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isDragHandlePressed, setIsDragHandlePressed] = useState(false);

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const longPressTimer = useRef(null);
  const itemRef = useRef(null);
  const dragHandleLongPressTimer = useRef(null);

  // --------------------------------------------------------------------------
  // 📊 DATOS FORMATEADOS
  // --------------------------------------------------------------------------
  const {
    id,
    clientName,
    clientPhone,
    lastMessage,
    unreadCount,
    isStarred,
    isArchived,
  } = conversation;

  const displayName = formatContactName(clientName);
  const displayTime = formatConversationTime(lastMessage?.timestamp);
  const displayPreview = formatMessagePreview(
    {
      content: {
        body: lastMessage?.body,
        type: lastMessage?.type,
      },
      direction: lastMessage?.direction,
    },
    { maxLength: 45, showPrefix: true }
  );
  const displayUnread = formatUnreadCount(unreadCount);

  // Iniciales del avatar
  const initials = clientName
    ? clientName
        .split(' ')
        .map(w => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

  // Color del avatar (basado en nombre para consistencia)
  const avatarColors = [
    'from-blue-500 to-blue-600',
    'from-emerald-500 to-emerald-600',
    'from-purple-500 to-purple-600',
    'from-amber-500 to-amber-600',
    'from-rose-500 to-rose-600',
    'from-cyan-500 to-cyan-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600',
  ];
  const avatarColor = avatarColors[
    (clientName || '').charCodeAt(0) % avatarColors.length
  ];

  // Estado del último mensaje
  const isOutbound = lastMessage?.direction === 'outbound';
  const isRead = lastMessage?.status === MESSAGE_STATUS.READ;
  const isDelivered = lastMessage?.status === MESSAGE_STATUS.DELIVERED;
  const isFailed = lastMessage?.status === MESSAGE_STATUS.FAILED;

  const StatusIcon = isFailed
    ? AlertCircle
    : isRead
      ? CheckCheck
      : isDelivered
        ? CheckCheck
        : Clock;

  const statusColor = isRead
    ? 'text-blue-500'
    : isFailed
      ? 'text-red-500'
      : 'text-gray-400';

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE SWIPE
  // --------------------------------------------------------------------------

  const handleDragStart = useCallback(() => {
    setIsSwiping(true);
  }, []);

  const handleDrag = useCallback((_, info) => {
    // Limitar el swipe a SWIPE_MAX
    const x = Math.max(-SWIPE_MAX, Math.min(SWIPE_MAX, info.offset.x));
    setSwipeX(x);
  }, []);

  const handleDragEnd = useCallback((_, info) => {
    setIsSwiping(false);

    const { offset, velocity } = info;
    const swipeVelocity = Math.abs(velocity.x);

    // Swipe derecho (eliminar) - solo si no está en modo selección
    if (!selectionMode && offset.x > SWIPE_THRESHOLD && swipeVelocity > 0.3) {
      setSwipeX(SWIPE_MAX);
      // Vibrar si está disponible
      if (navigator.vibrate) navigator.vibrate(10);
      setTimeout(() => {
        onDelete?.(id);
        setSwipeX(0);
      }, 200);
      return;
    }

    // Swipe izquierdo (archivar) - solo si no está en modo selección
    if (!selectionMode && offset.x < -SWIPE_THRESHOLD && swipeVelocity > 0.3) {
      setSwipeX(-SWIPE_MAX);
      if (navigator.vibrate) navigator.vibrate(10);
      setTimeout(() => {
        onArchive?.(id, !isArchived);
        setSwipeX(0);
      }, 200);
      return;
    }

    // Volver a posición original
    setSwipeX(0);
  }, [selectionMode, id, isArchived, onDelete, onArchive]);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE TOQUE
  // --------------------------------------------------------------------------

  const handlePress = useCallback(() => {
    if (selectionMode) {
      onToggleSelect?.(id);
      return;
    }
    if (isSwiping) return;
    onPress?.(conversation);
  }, [selectionMode, isSwiping, id, conversation, onPress, onToggleSelect]);

  const handleLongPressStart = useCallback(() => {
    if (selectionMode) return;
    longPressTimer.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(15);
      onLongPress?.(id);
    }, PERFORMANCE.LONG_PRESS_MS);
  }, [selectionMode, id, onLongPress]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE ACCIONES RÁPIDAS
  // --------------------------------------------------------------------------

  const handleCall = useCallback((e) => {
    e.stopPropagation();
    if (clientPhone) {
      window.open(`tel:${clientPhone.replace(/\D/g, '')}`, '_self');
    }
  }, [clientPhone]);

  const handleQuickStar = useCallback((e) => {
    e.stopPropagation();
    onStar?.(id, !isStarred);
    if (navigator.vibrate) navigator.vibrate(5);
  }, [id, isStarred, onStar]);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE DRAG HANDLE (Mobile: long press activa drag)
  // --------------------------------------------------------------------------

  const handleDragHandleTouchStart = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // En mobile, activar drag con long press en la manija
    if (isMobile && !selectionMode) {
      dragHandleLongPressTimer.current = setTimeout(() => {
        setIsDragHandlePressed(true);
        if (navigator.vibrate) navigator.vibrate(10); // Haptic feedback
        // Iniciar drag programáticamente
        if (listeners?.onPointerDown) {
          listeners.onPointerDown(e);
        }
      }, 200); // 200ms para diferenciar de tap
    }
  }, [isMobile, selectionMode, listeners]);

  const handleDragHandleTouchEnd = useCallback((e) => {
    e.stopPropagation();
    if (dragHandleLongPressTimer.current) {
      clearTimeout(dragHandleLongPressTimer.current);
      dragHandleLongPressTimer.current = null;
    }
    setIsDragHandlePressed(false);
  }, []);

  // --------------------------------------------------------------------------
  // 🎨 RENDER
  // --------------------------------------------------------------------------

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={`
        relative overflow-hidden
        will-change-transform
        ${isDragging ? 'z-50 scale-[1.02] shadow-2xl' : 'z-auto'}
        ${isOver ? 'border-l-4 border-green-500 bg-green-50' : ''}
        transition-shadow duration-200
      `}
      {...attributes}
    >
      {/* ================================================================== */}
      {/* FONDOS DE SWIPE (detrás del item) */}
      {/* ================================================================== */}
      <div className="absolute inset-0 flex">
        {/* Swipe derecho: Eliminar (rojo) */}
        <motion.div
          className={`${SWIPE_DELETE_BG} flex items-center justify-end px-5 w-full h-full`}
          initial={{ opacity: 0 }}
          animate={{ opacity: swipeX > 20 ? Math.min(swipeX / SWIPE_MAX, 1) : 0 }}
          transition={{ duration: 0.15 }}
        >
          <Trash2 size={22} className="text-white" />
        </motion.div>

        {/* Swipe izquierdo: Archivar (verde) */}
        <motion.div
          className={`${SWIPE_ARCHIVE_BG} flex items-center justify-start px-5 w-full h-full`}
          initial={{ opacity: 0 }}
          animate={{ opacity: swipeX < -20 ? Math.min(Math.abs(swipeX) / SWIPE_MAX, 1) : 0 }}
          transition={{ duration: 0.15 }}
        >
          <Archive size={22} className="text-white" />
        </motion.div>
      </div>

      {/* ================================================================== */}
      {/* ITEM PRINCIPAL (swipeable) */}
      {/* ================================================================== */}
      <motion.div
        ref={itemRef}
        className={`
          relative flex items-center gap-3 px-4 py-3 cursor-pointer
          bg-card hover:bg-gray-50 active:bg-gray-100
          border-b border-gray-100
          transition-colors duration-150
          ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : ''}
          ${isArchived ? 'opacity-70' : ''}
          ${isDragging ? 'opacity-50' : ''}
        `}
        style={{ x: swipeX }}
        drag={selectionMode || isDragging ? false : 'x'}
        dragConstraints={{ left: -SWIPE_MAX, right: SWIPE_MAX }}
        dragElastic={0.3}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onClick={handlePress}
        onTouchStart={handleLongPressStart}
        onTouchEnd={handleLongPressEnd}
        onTouchCancel={handleLongPressEnd}
        onMouseDown={handleLongPressStart}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        role="button"
        tabIndex={0}
        aria-label={`Conversa com ${displayName}`}
        aria-selected={isSelected}
      >
        {/* ============================================================== */}
        {/* MANIJA DE ARRASTRE (DRAG HANDLE) */}
        {/* ============================================================== */}
        <div
          className={`
            flex-shrink-0
            hidden sm:flex items-center justify-center
            w-8 h-full -ml-2 -mr-1
            cursor-grab active:cursor-grabbing
            text-gray-300 hover:text-gray-500
            transition-colors duration-150
            ${isDragging ? 'text-blue-500' : ''}
            ${isMobile ? 'opacity-50' : 'opacity-100'}
          `}
          {...listeners}
          onTouchStart={handleDragHandleTouchStart}
          onTouchEnd={handleDragHandleTouchEnd}
          onTouchCancel={handleDragHandleTouchEnd}
          aria-label="Arrastrar para reordenar"
          title="Arrastrar para reordenar"
        >
          <GripVertical 
            size={18} 
            className={`
              transition-transform duration-200
              ${isDragHandlePressed ? 'scale-125 text-blue-500' : ''}
            `}
          />
        </div>

        {/* ============================================================== */}
        {/* CHECKBOX SELECCIÓN (visible en modo selección) */}
        {/* ============================================================== */}
        <AnimatePresence>
          {selectionMode && (
            <motion.div
              initial={{ scale: 0, width: 0 }}
              animate={{ scale: 1, width: 'auto' }}
              exit={{ scale: 0, width: 0 }}
              className="flex-shrink-0"
            >
              <div
                className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center
                  transition-all duration-200
                  ${isSelected
                    ? 'bg-blue-500 border-blue-500'
                    : 'border-gray-300 bg-card'
                  }
                `}
              >
                {isSelected && <Check size={14} className="text-white" />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ============================================================== */}
        {/* AVATAR */}
        {/* ============================================================== */}
        <div className="flex-shrink-0 relative">
          <div
            className={`
              w-12 h-12 rounded-full bg-gradient-to-br ${avatarColor}
              flex items-center justify-center
              shadow-sm
            `}
          >
            <span className="text-white font-semibold text-sm select-none">
              {initials}
            </span>
          </div>

          {/* Indicador online (preparado para futuro) */}
          {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" /> */}
        </div>

        {/* ============================================================== */}
        {/* CONTENIDO CENTRAL */}
        {/* ============================================================== */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Línea superior: Nombre + Hora */}
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="font-semibold text-sm text-gray-900 truncate">
                {displayName}
              </h3>

              {/* Indicador de favorito */}
              {isStarred && (
                <Star size={12} className="text-amber-400 flex-shrink-0 fill-amber-400" />
              )}

              {/* Indicador de archivado */}
              {isArchived && (
                <Archive size={12} className="text-gray-400 flex-shrink-0" />
              )}
            </div>

            <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
              {displayTime}
            </span>
          </div>

          {/* Línea inferior: Preview + Badge no leídos */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 min-w-0">
              {/* Icono de estado (solo para mensajes salientes) */}
              {isOutbound && !isFailed && (
                <StatusIcon
                  size={14}
                  className={`flex-shrink-0 ${statusColor}`}
                />
              )}

              {/* Indicador de fallo */}
              {isFailed && (
                <AlertCircle size={14} className="flex-shrink-0 text-red-500" />
              )}

              <p className="text-sm text-gray-500 truncate">
                {displayPreview || 'Sem mensagens'}
              </p>
            </div>

            {/* Badge no leídos */}
            {displayUnread && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`
                  flex-shrink-0 min-w-[20px] h-5 px-1.5
                  rounded-full bg-green-500
                  text-white text-xs font-bold
                  flex items-center justify-center
                `}
              >
                {displayUnread}
              </motion.span>
            )}
          </div>
        </div>

        {/* ============================================================== */}
        {/* ACCIONES RÁPIDAS (Desktop: hover, Mobile: siempre visible mini) */}
        {/* ============================================================== */}
        <div className="hidden md:flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {/* Llamar */}
          {clientPhone && (
            <button
              onClick={handleCall}
              className="p-1.5 rounded-full hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
              aria-label={`Ligar para ${displayName}`}
            >
              <Phone size={16} />
            </button>
          )}

          {/* Favorito */}
          <button
            onClick={handleQuickStar}
            className={`p-1.5 rounded-full hover:bg-amber-50 transition-colors ${
              isStarred ? 'text-amber-400' : 'text-gray-400 hover:text-amber-500'
            }`}
            aria-label={isStarred ? 'Remover favorito' : 'Favoritar'}
          >
            <Star size={16} className={isStarred ? 'fill-amber-400' : ''} />
          </button>
        </div>
      </motion.div>
    </div>
  );
});

// ============================================================================
// 🏷️ DISPLAY NAME
// ============================================================================
ConversaItem.displayName = 'ConversaItem';

// ============================================================================
// 📤 EXPORTACIÓN
// ============================================================================

export { ConversaItem };
export default ConversaItem;